import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import tw, { css, styled } from "twin.macro"
import PageTitle from "../pageTitle"
import { useRouter } from "next/router"
import slugify from "slugify"
import WorksNav, { AllFilters } from "./worksNav"
import { WorksFields } from "../work"
import { useCTX } from "../../util/context"

const StyledWorkItem = styled("li")(() => [
  tw`
    col-span-2
    tablet:col-span-4
    desktop:col-span-3
    flex justify-center items-center
    py-4
    tablet:(py-8)
    desktop:(py-12)
  `,
  css`
    a:hover {
      ${tw`no-underline`}
      h2 {
        span {
          ${tw`border-b-2 border-dotted border-rpd-blue text-rpd-blue`}
        }
      }
    }
  `,
])

const StyledImage = styled("div")(() => [
  tw`
    w-full
    flex justify-center items-center relative
    max-h-[300px]
    h-[300px]
  `,
  css`
    img {
      ${tw`max-h-[300px]`}
      object-fit: scale-down;
    }
  `,
])

const description = [
  {
    key: "overview",
    value:
      "Selected works across mediums by Richard Pousette-Dart, arranged in chronological order, from a career that spanned more than six decades.  These selections serve as an introduction to the artist’s work, rather than as a comprehensive catalogue of his output.",
  },
  { key: "paintings", value: "Selected paintings, arranged by date." },
  { key: "works-on-paper", value: "Selected works on paper, including studio notebooks, arranged by date." },
  { key: "sculpture", value: "Selected sculpture, including brasses, arranged by date." },
  { key: "photographs", value: "Selected photographic prints, arranged by date." },
]

interface WorkCardProps {
  priority: boolean
  item: any
}
const WorkCard = (props: WorkCardProps) => {
  const { item, priority } = props
  const { id, title, image_url, image_preview, caption } = item.fields

  if (!image_preview || !image_url) {
    return <></>
  }

  const thisImage = image_preview[0].thumbnails.large
  const width = thisImage.width
  const height = thisImage.height
  const placeholderImgUrl = image_url + "?w=25&h=25"

  const altText = caption
    ? `${caption.toString()} – The Richard Pousette-Dart Foundation`
    : `The Richard Pousette-Dart Foundation`

  return (
    <StyledWorkItem>
      <Link
        href={{ pathname: "/works/[id]", query: { id: id } }}
        passHref
        tw="flex flex-col justify-between items-center h-full space-y-4"
      >
        <StyledImage>
          <Image
            key={id}
            src={image_url}
            alt={altText}
            width={width}
            height={height}
            priority={priority}
            placeholder="blur"
            blurDataURL={placeholderImgUrl}
            sizes="(max-width: 768px) 50vw,
              (max-width: 1200px) 35vw,
              25vw"
          />
        </StyledImage>
        <div tw="flex-grow-0 w-full">
          <h2 tw="text-sm font-normal font-sans text-center">
            <span>{title}</span>
          </h2>
        </div>
      </Link>
    </StyledWorkItem>
  )
}

interface WorkIndexProps {
  initialWorks: Array<WorksFields>
}

const WorkIndex = ({ initialWorks }: WorkIndexProps) => {
  const { pathname } = useRouter()
  const currentPath = pathname.replace(`/works/`, "")

  const [worksNav, setWorksNav] = useState<string>(
    currentPath == "/works" ? slugify(AllFilters.Overview, { replacement: "", lower: true }) : currentPath,
  )

  const { workctx, setWorkCtx } = useCTX()

  useEffect(() => {
    setWorkCtx(worksNav)
  }, [worksNav])

  const getDesc = () => {
    const text = description.find((item) => item.key === worksNav)
    return text?.value
  }

  return (
    <section tw="py-10 space-y-10">
      <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
        <div tw="grid grid-cols-4 desktop:(grid-cols-12) gap-4 desktop:(gap-6 gap-y-0)">
          <div tw="col-span-4 tablet-lg:col-span-12">
            <PageTitle title="Works" />

            <WorksNav {...{ worksNav, setWorksNav }} />

            <p tw="px-2 pt-8 max-w-screen-tablet-lg text-sm font-sans tablet:(pt-6)">{getDesc()}</p>

            <ul tw="grid grid-cols-4 tablet:(grid-cols-12) gap-4 desktop:(gap-6 gap-y-0)">
              {initialWorks &&
                initialWorks.map((item: any, i: number) => {
                  const priority = i < 4 && true
                  const cardProps = { item, priority }
                  return <WorkCard {...cardProps} key={item.id} />
                })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

export default WorkIndex
